'use client';

import { c as _c } from "react/compiler-runtime";
import type { UsedSkill } from '@shared/all/domains/skills';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, ScrollText, X } from 'lucide-react';
import { type RefObject, useEffect, useState } from 'react';
import { client } from '../../utils/trpc';

// The skill picker in the research input (.specs/skills.md): a dropdown with a
// search box, floating above the input like Oakbox's model menu. Opened by the
// Skills button or by typing "/" at the start of a word. Picking toggles — a
// picked skill carries a check, and picking it again takes it off — and the
// picks ride on the next message sent. The assistant can still find skills on
// its own; this is for when the person already knows which one.

/** How many matches the menu lists: a shortlist, not a browser. */
const LIMIT = 8;
export const SkillMenu = t0 => {
  const $ = _c(45);
  const {
    anchor,
    picks,
    onToggle,
    onClose
  } = t0;
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(0);
  let t1;
  if ($[0] !== search) {
    t1 = search.trim();
    $[0] = search;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const query = t1;
  let t2;
  if ($[2] !== query) {
    t2 = ["skillsMenu", query];
    $[2] = query;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== query) {
    t3 = () => unwrapFromResult(client.skills.list.query({
      ...(query ? {
        query
      } : {}),
      limit: LIMIT
    }, {
      context: {
        silenceErrors: true
      }
    }));
    $[4] = query;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t2,
      placeholderData: keepPreviousData,
      queryFn: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const list = useQuery(t4);
  let t5;
  if ($[9] !== list.data?.skills) {
    t5 = list.data?.skills ?? [];
    $[9] = list.data?.skills;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const rows = t5;
  const at = Math.min(active, Math.max(rows.length - 1, 0));
  let t6;
  let t7;
  if ($[11] !== anchor || $[12] !== onClose) {
    t6 = () => {
      const onDown = e => {
        if (!anchor.current?.contains(e.target as Node)) {
          onClose();
        }
      };
      document.addEventListener("pointerdown", onDown);
      return () => document.removeEventListener("pointerdown", onDown);
    };
    t7 = [anchor, onClose];
    $[11] = anchor;
    $[12] = onClose;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== onToggle) {
    t8 = row => onToggle({
      uuid: row.uuid,
      title: row.title
    });
    $[15] = onToggle;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const toggle = t8;
  let t9;
  if ($[17] !== at || $[18] !== onClose || $[19] !== rows || $[20] !== toggle) {
    t9 = e_0 => {
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        onClose();
        return;
      }
      if (rows.length === 0) {
        return;
      }
      if (e_0.key === "ArrowDown" || e_0.key === "ArrowUp") {
        e_0.preventDefault();
        const step = e_0.key === "ArrowDown" ? 1 : rows.length - 1;
        setActive((at + step) % rows.length);
        return;
      }
      if (e_0.key === "Enter" || e_0.key === "Tab") {
        e_0.preventDefault();
        const row_0 = rows[at];
        if (row_0) {
          toggle(row_0);
        }
      }
    };
    $[17] = at;
    $[18] = onClose;
    $[19] = rows;
    $[20] = toggle;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const onKeyDown = t9;
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_1 => {
      setSearch(e_1.target.value);
      setActive(0);
    };
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== onKeyDown || $[24] !== search) {
    t11 = <input autoFocus={true} value={search} placeholder="Search skills" aria-label="Search skills" onChange={t10} onKeyDown={onKeyDown} className="bg-background mb-1 w-full rounded-lg border border-ds-border-standard px-3 py-1.5 text-sm text-textPrimary outline-none focus:border-primary" />;
    $[23] = onKeyDown;
    $[24] = search;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== at || $[27] !== picks || $[28] !== rows || $[29] !== toggle) {
    let t13;
    if ($[31] !== at || $[32] !== picks || $[33] !== toggle) {
      t13 = (row_1, i) => {
        const on = picks.some(p => p.uuid === row_1.uuid);
        return <button key={row_1.uuid} type="button" role="option" aria-selected={on} onMouseDown={_temp} onPointerEnter={() => setActive(i)} onClick={() => toggle(row_1)} className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-textPrimary ${i === at ? "bg-hover" : ""}`}><span className="min-w-0 flex-1 truncate">{row_1.title}</span><Check className={`size-4 shrink-0 text-primary ${on ? "opacity-100" : "opacity-0"}`} /></button>;
      };
      $[31] = at;
      $[32] = picks;
      $[33] = toggle;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    t12 = rows.map(t13);
    $[26] = at;
    $[27] = picks;
    $[28] = rows;
    $[29] = toggle;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[35] !== list.isError || $[36] !== list.isLoading || $[37] !== rows.length) {
    t13 = !list.isLoading && rows.length === 0 && <p className="px-3 py-2 text-sm text-textSecondary">{list.isError ? "Could not load skills." : "No skill matches."}</p>;
    $[35] = list.isError;
    $[36] = list.isLoading;
    $[37] = rows.length;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t12 || $[40] !== t13) {
    t14 = <div role="listbox" aria-label="Skills" className="max-h-72 overflow-y-auto">{t12}{t13}</div>;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t11 || $[43] !== t14) {
    t15 = <div className="absolute bottom-full left-0 z-50 mb-2 w-72 max-w-full rounded-xl border border-ds-border-standard bg-background-light p-1 text-left shadow-lg">{t11}{t14}</div>;
    $[42] = t11;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  return t15;
};

/** The button beside the input that opens the menu, with the count picked. */
export const SkillsButton = t0 => {
  const $ = _c(7);
  const {
    count,
    open,
    onPress
  } = t0;
  const t1 = `inline-flex shrink-0 items-center gap-1 rounded-full border px-3 py-2 text-xs font-semibold ${open || count > 0 ? "border-violet-500/40 bg-violet-500/10 text-textPrimary" : "border-ds-border-standard text-textSecondary"} hover:bg-hover`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ScrollText className="size-3.5 text-violet-500" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = count > 0 ? `Skills · ${count}` : "Skills";
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronDown className="size-3.5" />;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== onPress || $[3] !== open || $[4] !== t1 || $[5] !== t3) {
    t5 = <button type="button" aria-label="Choose skills" aria-expanded={open} onClick={onPress} className={t1}>{t2}{t3}{t4}</button>;
    $[2] = onPress;
    $[3] = open;
    $[4] = t1;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};

/** The picks, as chips that come off when pressed. */
export const SkillChips = t0 => {
  const $ = _c(3);
  const {
    picks,
    onRemove
  } = t0;
  let t1;
  if ($[0] !== onRemove || $[1] !== picks) {
    t1 = picks.length > 0 ? <div className="flex flex-wrap gap-1.5">{picks.map(pick => <button key={pick.uuid} type="button" aria-label={`Remove ${pick.title}`} onClick={() => onRemove(pick.uuid)} className="inline-flex max-w-full items-center gap-1 rounded-full border border-violet-500/40 bg-violet-500/10 px-2.5 py-1 text-xs text-textPrimary hover:bg-violet-500/20"><ScrollText className="size-3 shrink-0 text-violet-500" /><span className="truncate">{pick.title}</span><X className="size-3 shrink-0 text-textSecondary" /></button>)}</div> : null;
    $[0] = onRemove;
    $[1] = picks;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function _temp(e_2) {
  return e_2.preventDefault();
}